<template>
    <div class="flow-object-filter-list">
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>

        <div v-if="gridLoadedEmpty" key="gridLoadedEmpty">
            <slot name="empty">
                <div class="d-flex w-100 min-height-200 border rounded-lg bg-white-40 align-items-center justify-content-center">
                    <div class="text-muted text-uppercase font-weight-500">{{ emptyText }}</div>
                </div>
            </slot>
        </div>

        <div v-else-if="!gridCanMount" key="gridStillLoading" class="h-100">
            <slot name="loading">
                <busy-spinner :text="loadingText"></busy-spinner>
            </slot>
        </div>

        <div v-else key="gridCanMount">

            <div v-if="gridCanMount" class="py-2 d-flex justify-content-between align-items-end">
                <div class="">
                    <div class="d-flex gap-3 align-items-end">  <!-- Left side quick filters -->
                        <div v-if="!hideTimeFilter || !hideUserRoleFilter || !hidePresetsFilter" class="quick-filter">
                            <div class="h6 text-primary-darker">Quick<br>Filters</div>
                        </div>
                        <div v-if="!hideTimeFilter" class="quick-filter">
                            <label class="filter-label">Timeframe</label>
                            <PrimeDropdown 
                                :value="timeFilterKey" 
                                @input="handleExternalTimeFilterChanged"
                                :options="timeFilterOptions" 
                                optionLabel="label"
                                optionValue="key"
                                dataKey="key"
                                scrollHeight="250px"
                                :class="(timeFilterKey !== timeFilterReset) ? 'font-weight-bold' : ''"
                            />
                        </div>
                        
                        <div v-if="!hideUserRoleFilter" class="quick-filter">
                            <label class="filter-label">My Role</label>
                            <PrimeDropdown 
                                :value="userRoleKey" 
                                @input="handleExternalRoleFilterChanged"
                                :options="userRoleOptions" 
                                optionLabel="label"
                                optionValue="key"
                                dataKey="key"
                                :class="(userRoleKey !== userRoleReset) ? 'font-weight-bold' : ''"
                            />
                        </div>


                        <!-- PRESETS -->
                        <div v-if="!hidePresetsFilter && gridPresets && gridPresets.length" class="quick-filter">
                            <label class="filter-label">Presets</label>
                            <div class="p-inputgroup">
                                <PrimeCascadeSelect 
                                    :value="gridPresetSelected" 
                                    @input="handlePresetSelection"
                                    :options="gridPresets" 
                                    dataKey="key"
                                    optionLabel="label" 
                                    optionGroupLabel="label"
                                    :optionGroupChildren="['items']"
                                    placeholder="All Items"
                                    style="minWidth: 14rem"
                                    :class="gridPresetSelected ? 'font-weight-bold' : ''"
                                >
                                    <template #value="slotProps">
                                        <div class="">
                                            {{ lastSetPresetIsCurrent ? (slotProps.value ? slotProps.value.label : slotProps.placeholder ) : 'Custom' }}
                                        </div>
                                    </template>
                                </PrimeCascadeSelect>
                                <PrimeButton 
                                    icon="pi pi-times" 
                                    :disabled="!gridPresetSelected" 
                                    :class="gridPresetSelected ? '' : 'p-button-secondary p-button-outlined'" 
                                    class="p-button-sm"
                                    @click="resetPresetSelection"
                                />
                            </div>
                        </div>

                        <div class="quick-filter">
                            <label class="filter-label">Per Page</label>
                            <PrimeDropdown 
                                :value="paginationPageSize" 
                                @input="updatePagination"
                                :options="pageSizeOptions" 
                                class=""
                            />
                        </div>

                        <div class="text-center">
                            <div 
                                v-if="gridHasAnyFilters || hasAnyExternalFiltersApplied" 
                                class="text-primary-darker special-text font-size-70 text-center mb-1"
                            >
                                Filters Applied
                            </div>
                            <b-button 
                                :variant="isReset ? 'subtle-secondary' : 'sharp-primary'" 
                                size="sm" 
                                @click="resetFilters"
                            >Reset Filters / Grouping</b-button>
                        </div>

                    </div>
                </div>
                <div v-if="rowData.length">  <!-- BUTTONS / TOOLBAR -->
                    <div class="d-flex gap-2 justify-content-end">
                        <b-button variant="subtle-primary" size="sm" @click="reloadAll"><b-icon icon="arrow-counterclockwise" class="mr-1"></b-icon>Reload</b-button>
                        <!-- <b-button-group v-if="$currentUserInfo.user_is_staff" size="sm" class="">
                            <b-button variant="subtle-primary" @click="dumpGrid">Dump Grid</b-button>
                        </b-button-group> -->
                        <b-button-group size="sm" class="">
                            <b-button variant="subtle-primary" class="d-flex align-items-center gap-2" @click="exportExcel">
                                <!-- <b-icon variant="success" icon="file-earmark-excel" class="mr-1"></b-icon> -->
                                <span class="fiv-sqo fiv-icon-xls font-size-100"></span>
                                <span>Export to Excel</span>
                            </b-button>
                        </b-button-group>
                    </div>
                    <div class="d-flex gap-2 mt-1 justify-content-end">
                        <b-button-group size="sm" class="">
                            <b-button variant="subtle-primary" @click="resetColumns"><b-icon icon="arrow-counterclockwise" class="mr-1"></b-icon>Reset Columns</b-button>
                            <!-- <b-button variant="subtle-primary" @click="resetRowHeights">Reset Heights</b-button> -->
                            <!-- <b-button variant="subtle-primary" @click="autoSizeAll">Auto-Size</b-button> -->
                            <b-button variant="subtle-primary" @click="autoSizeAll"><b-icon icon="arrow-left-short"></b-icon><b-icon icon="arrow-right-short" class="mr-1"></b-icon>Auto-Size Columns</b-button>
                            <!-- <b-button variant="subtle-primary" @click="sizeColumnsToFit"><b-icon icon="arrow-left-short"></b-icon><b-icon icon="arrow-right-short" class="mr-1"></b-icon>Fit Columns</b-button> -->
                        </b-button-group>
                    </div>
                </div>
            </div>

            <div 
                class="mb-4" 
                :style="gridWrapperStyle"
            >
                <ag-grid-vue
                    v-if="gridCanMount"
                    key="gridCanMount"
                    ref="theGrid"
                    class="ag-theme-alpine w-100 h-100"
                    :style="gridStyle"
                    :gridOptions="gridOptions"
                    :sideBar="sideBar"
                    :statusBar="statusBar"
                    :defaultColDef="defaultColDef"
                    :columnTypes="columnTypes"
                    :columnDefs="columnDefs"
                    :context="gridContext"
                    :rowData="rowData"
                    :animateRows="true"
                    :enableCharts="enableCharts"
                    :enableRangeSelection="enableRangeSelection"
                    :domLayout="domLayout"
                    :autoGroupColumnDef="autoGroupColumnDef"
                    :suppressDragLeaveHidesColumns="true"
                    :suppressMakeColumnVisibleAfterUnGroup="true"
                    :suppressContextMenu="suppressContextMenu"
                    :rowGroupPanelShow="rowGroupPanelShow"
                    :pagination="pagination"
                    :paginationPageSize="paginationPageSize"
                    :isExternalFilterPresent="isExternalFilterPresent"
                    :doesExternalFilterPass="doesExternalFilterPass"
                    @column-resized="updateRowHeights"
                    @newColumnsLoaded="handleNewColumnsLoaded"
                    @column-visible="handleColumnVisible"
                    @filter-changed="handleFilterChanged"
                    @columnRowGroupChanged="handleColumnRowGroupChanged"
                    @cell-double-clicked="handleCellDoubleClicked"
                    @firstDataRendered="firstDataRendered"
                    @grid-ready="onGridReady"
                ></ag-grid-vue>

                <!-- <div v-else key="gridCannotMount" class="h-100">
                    <busy-spinner text="Loading..."></busy-spinner>
                </div> -->  <!-- moved this up to top level -->

                <div id="myChart" class="ag-theme-alpine my-chart"></div>

                <!-- <div class="d-flex gap-5">
                    <div class="">
                        <h6>Filters? {{ gridHasAnyFilters ? 'yes' : 'no'}}</h6>
                        <pre>{{ currentFilters }}</pre>    
                    </div>
                    <div class="">
                        <h6>Grouping {{ gridHasAnyGrouping ? 'yes' : 'no'}}</h6>
                        <pre>{{ currentGroupingCols }}</pre>
                    </div>
                    <div class="">
                        <h6>Diff Filters Grouping</h6>
                        <pre>current : {{ currentFiltersGroupingStringified }}</pre>
                        <pre>last set: {{ lastSetFiltersGroupingStringified }}</pre>
                        SAME? {{ lastSetPresetIsCurrent ? 'YES' : 'NO' }}
                    </div>
                    <div class="">
                        <div class="">Time filter current: {{ lastSetTimeFilterIsCurrent ? 'YES' : 'NO' }}</div>
                        <div class="">User Role current: {{ lastSetUserRoleFilterIsCurrent ? 'YES' : 'NO' }}</div>
                    </div>
                </div> -->


            </div>
        
        </div>

    </div>
</template>

<script>
import { errorHandling, currentUserInfoMixin, tenantInfoMixin } from '../../js/custom-vue-mixins';
import { 
    //FormatDateHelper, 
    //FormatDateTimeLocal, 
    DurationSecondsToHumanized,
    FormatTimeHelper 
} from '../../js/filters-helpers';
import { 
    getFlowObjectApi,
} from '../../js/api-service';
import { AgGridVue } from 'ag-grid-vue';
import get from 'lodash/get';
import { DateTime } from 'luxon';
import Vue from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
//import PrimeCalendar from 'primevue/calendar';
import PrimeCascadeSelect from 'primevue/cascadeselect';
import PrimeButton from 'primevue/button';
import PrimeDropdown from 'primevue/dropdown';

const toJSDate = (isoDateTime) => {  // ag-grid date filters want JS Date objects
    if (!isoDateTime) {
        return null;
    }
    const luxonDateTime = DateTime.fromISO(isoDateTime);
    return luxonDateTime.isValid ? luxonDateTime.toJSDate() : null;
};
const formatDate = (jsDate) => {
    if (!jsDate) {
        return '';
    }
    const luxonDateTime = DateTime.fromJSDate(jsDate);
    return luxonDateTime.isValid ? luxonDateTime.toLocaleString(DateTime.DATE_SHORT) : '';
};
const formatDateTime = (jsDate) => {
    if (!jsDate) {
        return '';
    }
    const luxonDateTime = DateTime.fromJSDate(jsDate);
    return luxonDateTime.isValid ? luxonDateTime.toLocaleString(DateTime.DATETIME_SHORT) : '';
};

// VUE COMPONENTS
const SmStateRenderer = Vue.extend({  // pass in 
    computed: {
        smStateObj() {
            return this.params?.context?.sm_states?.[this.params.value] ?? {};
        },
        badgeVariant() {
            return this.smStateObj.color_variant;
        },
        badgeTitle() {
            return this.smStateObj.badge_title;
        }
    },
    template: `
        <div class="text-center">
            <b-badge v-if="badgeVariant && badgeTitle" :variant="badgeVariant">{{badgeTitle}}</b-badge>
            <span v-else>{{ params.valueFormatted || params.value }}</span>
        </div>
    `
});


export default {
    name: 'FlowObjectFilterList',
    components: {
        AgGridVue,
        //PrimeCalendar,
        PrimeCascadeSelect,
        PrimeButton,
        PrimeDropdown,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
        tenantInfoMixin,
    ],
    props: {
        flowObjectClass: {
            type: String,
            required: true,
        },
        flowSubtype: {
            type: String,
            default: null,
        },
        // ADD PROPS TO CONTROL LIST
        queryParams: {  // object of FIXED query_params to send to backend, must be supported by filterset_fields / ordering_fields for now 
            type: Object,  // likely replace this (or augment) with ag-grid specific filter objects 
            required: false,
        },
        emptyText: {  // can also use #empty slot, which has priority
            type: String,
            default: 'No data to show',
        },
        loadingText: {  // can also use #loading slot, which has priority
            type: String,
            default: 'Loading...',
        },
        // filters to show/hide, cols to show/hide, buttons to show/hide, ag-grid accessories to show/hide, field_group / field_list to fetch, backend must support it
        // row grouping enabled?  ag-grid col filters enabled?
        hideTimeFilter: {
            type: Boolean,
            default: false,
        },
        hideUserRoleFilter: {
            type: Boolean,
            default: false,
        },
        hidePresetsFilter: {
            type: Boolean,
            default: false,
        },
        // if you add more show/hide filters, make sure to update logic in template for showing/hiding 'Quick Filters' text
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            /// 
            //loadingMeta: false,
            loadingList: false,
            //meta: {},
            gridWrapperStyle: {width: '100%'},  // set it in beforeMount - not an ag-grid thing - set height/min-height at least - also relates to pagination
            gridStyle: {},  // on the grid element itself - gets updated when you change pagination 
            // GRID
            gridCanMount: false,  // set true when you have everything ready from API
            gridContext: null,
            defaultColDef: null,
            columnDefs: null,
            columnTypes: null,
            rowData: null,
            gridOptions: null,
            sideBar: null,
            statusBar: null,
            enableCharts: true,
            enableRangeSelection: false,  // must be true if you want users to be able to select areas for charting... but that will probably be horrible... need to do it yourself
            suppressContextMenu: true,  // turning off for entire grid until you can figure out how to do it per-column
            domLayout: 'autoHeight',  // options are 'normal' or 'autoHeight' or printing I think... make sure to also set height/style of wrapper and grid when changing this... all must work together
            gridApi: null,
            gridColumnApi: null,
            rowGroupPanelShow: null,  // options are 'always', 'never', 'onlyWhenGrouping'
            autoGroupColumnDef: null,  
            pagination: true,
            paginationPageSize: 15,
            paginationAutoPageSize: false,  // if true, paginationPageSize is not used, sets based on size of browser
            pageSizeOptions: [15, 25, 50],  
            // EXTRA THINGS
            isSomethingOn: false,  // dummy for now
            hasSomething: false,  // dummy for now
            // Lookups - id/value and label - PUT IN COLS FROM BACKEND INSTEAD
            // smStates: {},
            // tenantUsers: {},
            // vendors: {},
            // FILTER VALUES
            minJsDate: null, // IF you have EXTERNAL date/time filter, set the MINIMUM here (as JS Date)
            maxJsDate: null, // see above... the max - JS Date us
            onlyHasObjectRole: false, // set true to enforce 'has_object_role' from userRole filter 
            // PRESETS
            gridPresets: [],  // presets for filters/grouping generated by backend!
            gridPresetSelected: null,  // will be the entire preset object -- need it that way to show things corrently in cascade select slots
            lastSetFiltersGrouping: {},  // object with { filters, grouping } - for change detection
            lastSetFiltersGroupingStringified: '',  // updated when we select a preset
            currentFilters: {},  // make sure always an object..
            currentGroupingCols: [],
            dateRange: null,  // from range of date picker - later
            timeFilterOptions: [  // NOTE - for now, we have hard-coded values for each of these in filter set method below
                {key: '__ALL__', label: 'All time', default: true},
                {key: 'today', label: 'Today'},
                {key: 'yesterday', label: 'Yesterday'},
                {key: 'last48hours', label: 'Last 48 hours'},
                {key: 'last1week', label: 'Last 1 week'},
                {key: 'last1month', label: 'Last 1 month'},
            ],
            timeFilterReset: '__ALL__',
            timeFilterInitial: '__ALL__',  // allows initial to be different than the 'reset' value 
            timeFilterKey: '__ALL__',  // initial 
            lastSetTimeFilter: null,  // for change tracking
            userRoleOptions: [  // NOTE - for now, we have hard-coded values for each of these in filter set method below
                {key: '__ALL__', label: 'All Items'},
                // each of these should likely be a column (likely constructed in serializer in backend) that returns a boolean
                {key: 'has_object_role', label: 'Involved'},
                // {key: 'is_created_by', label: 'Creator', filter: {'is_created_by': {filterType: 'set', values: [true]}}},
            ],
            userRoleReset: '__ALL__',
            userRoleInitial: '__ALL__',
            userRoleKey: '__ALL__',  // not using involved as default for now... its tricky 
            lastSetUserRoleFilter: null, // for change tracking 
        }
    },
    computed: {
        flowObjectApi() {
            return getFlowObjectApi(this.flowObjectClass, this.flowSubtype);
        },
        /* gridPresetsByKey() {
            // un-nest the nested gridPreset array - return object by key
            const byKey = {};
            this.gridPresets.forEach(grp => {
                // only one level of nesting for now!
                if (grp.items?.length) {
                    // each should have { key, label, type (item), value } - value has { grouping, filters, key, label } - redundant for now...
                    // we'll get to the 'value' and use that as values here
                    grp.items.forEach(grpItem => {
                        if (grpItem.value?.key) {
                            byKey[grpItem.value.key] = {...grpItem.value};
                        }
                    });
                }
            });
            return byKey;
        }, */
        gridHasAnyFilters() {  // GRID - not external
            return !isEmpty(this.currentFilters);
        },
        gridHasAnyGrouping() {
            return !isEmpty(this.currentGroupingCols);
        },
        currentFiltersGroupingStringified() {
            return this.stringifyFiltersGrouping(this.currentFilters, this.currentGroupingCols);
        },
        lastSetPresetIsCurrent() {
            //return this.lastSetFiltersGroupingStringified ? 
            return isEmpty(this.lastSetFiltersGrouping) ? true : isEqual(this.lastSetFiltersGrouping, {filters: this.currentFilters, grouping: this.currentGroupingCols});
        },
        lastSetTimeFilterIsCurrent() {
            const current = this.currentFilters?.created_tenant_tz;
            return (isEmpty(current) && isEmpty(this.lastSetTimeFilter)) ? true : isEqual(current, this.lastSetTimeFilter);
        },
        lastSetUserRoleFilterIsCurrent() {
            const current = this.currentFilters?.has_object_role;
            return (isEmpty(current) && isEmpty(this.lastSetUserRoleFilter)) ? true : isEqual(current, this.lastSetUserRoleFilter);
        },
        hasAnyExternalFiltersApplied() {
            return this.isExternalFilterPresent();  // same thing we use as grid callback, so its consistent,
        },
        isReset() {
            return !this.gridHasAnyFilters && !this.gridHasAnyGrouping && !this.hasAnyExternalFiltersApplied;
        },
        colsNamesWithFilters() {
            // return an array of column headerNames that have filters applied -- you could show this
            const names = [];
            Object.keys(this.currentFilters).forEach(fld => {
                if (this.gridColumnApi) {
                    const name = this.gridColumnApi.getColumn(fld)?.colDef?.headerName;
                    if (name) {
                        names.push(name);
                    }
                }
            });
            return names;
        },
        gridHasRows() {
            // return boolean, do we have ANY rows?  does NOT check if loading or not
            return this.rowData && this.rowData.length;
        },
        gridLoadedEmpty() {
            // returns true when we know we loaded things, but we don't have any rows to show
            return !this.loadingList && this.gridCanMount && !this.gridHasRows;  // not loading, done loading, but no rows
        },
    },
    created() {
        // set initial filter values 
        this.timeFilterKey = this.timeFilterInitial;
        this.userRoleKey = this.userRoleInitial;
    },
    beforeMount() {
        // SET GRID / WRAPPER SIZES and LAYOUT
        /* this.gridWrapperStyle = {  // only set this if not doing autoHeight with pagination
            height: '500px',
            minHeight: '500px',
        }; */

        // set gridOptions
        this.gridOptions = {
            components: {},
            frameworkComponents: {
                smStateRenderer: SmStateRenderer,
            },
            getRowNodeId: function(data) {
                return data ? data.id : '';  // should return strings
            },  // use this to have grid use your own defined node IDs... or let it use its own.  should return strings.
            popupParent: document.querySelector('body'),  // needed so popups overflow outside the grid
            groupUseEntireRow: true,  // makes the group rows take up entire row, not just one column
        };
        this.statusBar = {
            statusPanels: [
                {
                    statusPanel: 'agTotalRowCountComponent',
                    align: 'left',
                },
                { statusPanel: 'agFilteredRowCountComponent' },
            ]
        };
        this.autoGroupColumnDef = {
            // minWidth: 200,  // 
            headerName: 'Group',
            field: 'title',  // NOTE - this is then fixed to QualityIssue - or other things with 'title' as the main col
        };
        this.rowGroupPanelShow = 'always'; // options are 'always', 'never', 'onlyWhenGrouping'
        this.gridContext = {
            componentParent: this,
        };
        this.sideBar = {
            position: 'left',  // left, right
            hiddenByDefault: false,
            defaultToolPanel: null,  // if null, then it starts closed -- or 'columns' or 'filters'
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                    toolPanelParams: {  // may want all these off... but experimenting with them on for now
                        suppressRowGroups: true,  // show in top bar instead
                        suppressValues: true,  // for aggregation... but we don't really have aggregatable values
                        suppressPivots: true,
                        suppressPivotMode: true,
                        suppressColumnFilter: false,
                        suppressColumnSelectAll: false,
                        suppressColumnExpandAll: false,
                        contractColumnSelection: false,  // if true, then groups start contracted instead of expanded - maybe make this dynamic based on category?
                    },
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                },
            ],
        };
        this.defaultColDef = {
            resizable: true,
            editable: false,
            sortable: true,
            filter: true,
            floatingFilter: true,  // not really needed.... not many text fields, just set filters
            //suppressMovable: true,
            suppressMenu: false,  // hides the menu for each col... set false if you want filters, or use inline/floating? filters
            menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],  // shown in order 'columnsMenuTab'
            wrapText: false,  // easier for autoSize
            autoHeight: false,  // not allowed if doing row spanning - turn off if wrapText is false - might be a big performance hit!
            singleClickEdit: false,
            enableCellChangeFlash: true,
            chartDataType: 'category',  // most should be this - if series, set those specifically in their type
            //enableRowGroup: true,  // allows it to be grouped via tool panel - only do it for certain data types
            //enablePivot: true,  // allows it to be pivot enabled via tool panel
            valueGetter: (params) => {
                // params.colDef.field is our dotted path 
                return get(params.data, params.colDef.field);  // use this instead of expression in case a row does not have that nested value.... lodash get has built-in optional chaining basically 
            },
        };
        this.columnTypes = {
            'linkCell': {  // add this type to whatever the first/main cell is to link to the detail page 
                cellRenderer: (params) => {
                    // wrap it in a link
                    //console.log('params in cellRenderer', params);
                    // if there is no valueFormatter, then valueFormatted will be null (kinda dumb)
                    // use internal_detail_url / external_detail_url ??? or permalink_url or full_permalink_url ???
                    // permalink_url should be more generic way of doing it
                    if (params.data?.permalink_url) {
                        return `<a href="${params.data.permalink_url}" class="font-weight-600">${params.valueFormatted || params.value}</a>`
                    } else {
                        return params.valueFormatted || params.value;
                    }
                },
                // Can't figure out how to turn off context menu for only one column... so we'll disable for entire grid for now
                /* onCellContextMenu: (params) => {
                    // cancel the event so context menu does not show
                    console.log('params from onCellContextMenu', params);
                    const evt = params.event;
                    if (evt && evt.cancelable) {
                        console.log('should now preventDefault');
                        evt.preventDefault(); // DOES NOT WORK
                    }
                }, */
            },
            'lookup': {
                // ACTUALLY DON'T NEED valueFormatter HERE -- IF YOU HAVE refData THEN GRID AUTOMATICALLY USES THAT IN valueFormatter
                // AND YOU CAN JUST USE THE VALUE GETTER FOR DEFAULT COLDEF
                // AND FOR EXCEL EXPORT, YOU CAN USE THE EXCEL CALLBACK TO FORMAT VALUES FROM refData
                enableRowGroup: true,
                enablePivot: true,
                filterParams: {  // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
                    buttons: ['reset'],
                    closeOnApply: false,
                },
            },
            'string': {
                enableRowGroup: true,  // there will likely be strings fields that are not lookups, but still lookup-ish... so default to true
                filter: 'agTextColumnFilter',
                filterParams: {  // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
                    buttons: ['clear'],
                    closeOnApply: true,
                },
            },  // add if/when needed
            'integer': {
                cellClass: 'number-cell',  // makes things right aligned
                enableRowGroup: true,  // makes senses for integers certainly... maybe not floats, but ints OK
                filter: 'agNumberColumnFilter',
                filterParams: {  // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
                    buttons: ['clear'],
                    closeOnApply: true,
                },
            },  // add if/when needed
            'money': {
                cellClass: 'number-cell',  // makes things right aligned
                filter: 'agNumberColumnFilter',
                filterParams: {  // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
                    buttons: ['clear'],
                    closeOnApply: true,
                },
                valueGetter: (params) => {
                    // might be null, or an object in many different formats, or djmoney DRF serializer field, which splits the amount/currency as separate fields (stupid)
                    // for now, we're building this for cases where you'll either have null, or a complex object like this:  { _type: 'Money', value: { amount: '1.05', currency: 'USD'}}
                    // if things come DIRECTLY from some JSONFields, you could possibly also have a _type: "Decimal" nested inside amount
                    // for valueGetter, we want to be able to sort things, so we return just the NUMBER itself
                    const val = get(params.data, params.colDef.field);  
                    if (!val) {
                        return null;
                    } else if (val['_type'] === 'Money') {
                        const amount = val.value?.amount?.value ?? val.value?.amount;  // go for the extra nested one first (Decimal nested in 'amount'), fall back to just having a string in 'amount'
                        // that might be a STRING (likely is) - need to convert to Number - also conserve the correct decimal places!  maybe pass that in somwhere?
                        // not needed... const currencyCode = val.value?.currency;
                        return Number(amount);
                    }
                    return val;  // otherwise just return whatever we got from the normal get() - likely undefined, which will be OK for the grid
                },
                valueFormatter: (params) => {
                    // might as well just start over getting things?  or we could get amount from params.value, which we got in valueGetter
                    // remember to handle things lik 0 correctly... falsey, but maybe valid amount / params.value 
                    const val = get(params.data, params.colDef.field);  
                    const defaultFormatted = null;
                    
                    if (!val) {
                        return defaultFormatted;
                    } else if (val['_type'] === 'Money') {
                        const amount = val.value?.amount?.value ?? val.value?.amount;  // go for the extra nested one first (Decimal nested in 'amount'), fall back to just having a string in 'amount'
                        // that might be a STRING (likely is) - need to convert to Number - also conserve the correct decimal places!  maybe pass that in somwhere?
                        const amountNumber = Number(amount);
                        const currencyCode = val.value?.currency;  // MUST have this
                        if (!currencyCode || Number.isNaN(amountNumber)) {
                            return defaultFormatted;
                        }
                        const theLocale = 'en-US';  // very small-minded of me... check browser instead?
                        const currencyFormat = {
                            style: 'currency',
                            currency: currencyCode,
                        };
                        return amountNumber.toLocaleString(theLocale, currencyFormat);
                    }
                    return defaultFormatted;  // just the final default... maybe something else is better
                }
            },  // add if/when needed
            'float': {
                cellClass: 'number-cell',  // makes things right aligned
                filter: 'agNumberColumnFilter',
                filterParams: {  // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
                    buttons: ['clear'],
                    closeOnApply: true,
                },
            },  // add if/when needed
            'decimal': {
                cellClass: 'number-cell',  // makes things right aligned
                filter: 'agNumberColumnFilter',
                filterParams: {  // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
                    buttons: ['clear'],
                    closeOnApply: true,
                },
            },  // add if/when needed
            'date': {
                cellClass: 'centered-cell',
                filter: 'agDateColumnFilter',
                filterParams: {  // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
                    buttons: ['clear', 'reset'],
                    closeOnApply: true,
                },
                enableRowGroup: true,  // makes sense for 'date' but maybe not 'datetime'
                valueGetter: (params) => {
                    // params.colDef.field is our dotted path 
                    const rawValue = get(params.data, params.colDef.field);  // use this instead of expression in case a row does not have that nested value.... lodash get has built-in optional chaining basically 
                    return toJSDate(rawValue);
                },
                valueFormatter: (params) => {
                    return formatDate(params.value);
                },
            },
            'time': {
                valueFormatter: (params) => {
                    return FormatTimeHelper(params.value);
                },
            },
            'datetime': {
                //hide: true,
                cellClass: 'centered-cell',
                filter: 'agDateColumnFilter',
                filterParams: {  // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
                    buttons: ['clear', 'reset'],
                    closeOnApply: true,
                },
                valueGetter: (params) => {
                    // params.colDef.field is our dotted path 
                    //console.log('params in datetime valueGetter', params);
                    const rawValue = get(params.data, params.colDef.field);  // use this instead of expression in case a row does not have that nested value.... lodash get has built-in optional chaining basically 
                    //console.log('rawValue', rawValue);
                    return toJSDate(rawValue);
                },
                valueFormatter: (params) => {
                    return formatDateTime(params.value);
                }
            },
            'seconds': {
                valueFormatter: (params) => {
                    return DurationSecondsToHumanized(params.value);
                },
            },
            'timedelta': {},  // add if/when needed (what do you get from backend??? seconds? iso duration?)
            'boolean': {
                cellClass: 'centered-cell',
                enableRowGroup: true,
                valueFormatter: (params) => {
                    switch (params.value) {
                        case true:
                            return 'true';  // doing this to be lazy and match what is used in filter dropdown... make better later
                        case false:
                            return 'false';
                        default:
                            return '';  // keep all else as empty string - maybe also do this in valueGetter?
                    } 
                }
            },  // add if/when needed (so you know how to specify what true/false actually mean)
            'unknown': {},  // if backend doesn't know what type it is, this is the default
            'list': {
                //enableRowGroup: true,  // does not work out of the box at least
            },  // list of names, etc.
        };
    },
    async mounted() {
        await this.reloadAll();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getList() {
            this.loadingList = true;
            const axiosConfig = {};
            if (this.queryParams) {
                axiosConfig['params'] = {...this.queryParams};
            }
            let resp;
            try {
                resp = await this.flowObjectApi.get_filter_list(axiosConfig);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                // pull off 'columns' and 'items', put the rest in grid context 
                const { columns, items, grid_presets, ...otherContext } = resp.data;
                this.gridContext = Object.assign({}, this.gridContext, otherContext);  // set a new object so its reactive... but preserve existing context
                // SET COLUMNS
                this.columnDefs = this.buildColDefs(columns);
                // STASH PRESETS
                this.gridPresets = (!!grid_presets && Array.isArray(grid_presets)) ? [...grid_presets] : [];
                // SET ITEMS/ROWS
                this.rowData = items.map(row => {
                    // MODIFY / ADD THINGS TO EACH ROW IF NEEDED
                    // we'll add a _count: 1 for making histograms?
                    // and a JS Date object in the tenant's timezone for easier date filtering
                    // const tenantTz = this.$tenantInfo.settings.timezone;  // do we need this? or does converting from ISO string in tenant_tz keep it in that timezone?
                    const createdTenantTzJs = row.created_tenant_tz ? DateTime.fromISO(row.created_tenant_tz).toJSDate() : null;
                    return {...row, _count: 1, createdTenantTzJs: createdTenantTzJs};  // add a '_count' attribute just for using in aggregation/pivot
                });
            }
            this.loadingList = false;
            this.beforeGridMount();
            this.gridCanMount = true;  // set true after first load... reset when reloading???
        },
        async reloadAll() {  // used in mounted() also
            this.gridCanMount = false;
            this.resetFilters();
            await this.getList();
        },
        beforeGridMount() {
            // we call this before the grid shows, in case we need to do any transformations to the grid based on the API data
            // can do things like configure cols / sideBar / group cols / etc. based on API data
        },
        // GRID CALLBACKS
        async onGridReady(event) {
            // do things now that grid is ready - this is called when we get gridReady event from grid
            // and that only happens after we set this.gridCanMount = true
            console.log('HELLO FROM this.onGridReady'); 
            console.log(event);
            await this.$nextTick();  // waits here...
            this.gridApi = this.gridOptions.api;
            this.gridColumnApi = this.gridOptions.columnApi;     
            this.afterGridAndDataReady();
        },
        afterGridAndDataReady() {
            // another hook point... or get rid of this and just put it inside onGridReady()
        },
        firstDataRendered(event) {
            console.log('firstDataRendered', event);
            this.autoSizeAll();
            // this.makeChart(event);  // TESTING
        },
        updateRowHeights(params) {
            if (params && (params.finished !== false)) {  // check if finished, if params has that... it may not
                params.api.resetRowHeights();
            } else if (!params) {
                // but maybe we called from a place without passing in params... 
                this.gridApi.resetRowHeights();
            }
        },
        resetRowHeights() {
            this.gridApi.resetRowHeights();
        },
        sizeColumnsToFit() {
            // use this for all sizing of columns to fit so you can do extra stuff first
            // should we first check all colGroups to make sure they're OK width?
            // instead... we'll check header heights for colGroups to since we have text-wrap on in those... and reset group header height if needed
            this.gridApi?.sizeColumnsToFit();  // will trigger updateRowHeights via event handler
        },
        autoSizeAll() {
            const skipHeader = true;
            if (this.gridColumnApi) {
                this.gridColumnApi.autoSizeAllColumns(null, skipHeader);  // there are other methods for only autoSize one or multiple
            }
        },
        resetColumns() {
            // reset original colDefs
            this.gridColumnApi.resetColumnState();
            // may also have to do resetColumnGroupState()
        },
        exportExcel() {
            // see docs: https://www.ag-grid.com/archive/25.3.0/vue-grid/excel-export-api/#interfaces
            const excelExportParams = {
                processCellCallback: (params) => {
                    // this should return a string to use in the export 
                    //console.log('exportExcelParams', params);
                    if (params.column.colDef.refData && params.value) {
                        // do we have refData?  grid does not use it when exporting to Excel by default... so we do it manually here
                        return params.column.colDef.refData[params.value];
                    } else {
                        return params.value;
                    }
                },
            };
            this.gridApi.exportDataAsExcel(excelExportParams);
        },
        handleColumnVisible() {
            // need to refresh cells when we show/hide cols to refresh css classes
            // event contains many things, including 'column' (column object that was updated) and 'columns' (array of column objects that was updated)
            // for now just refresh everything
            // NO - we have too many cols: this.sizeColumnsToFit(); // TODO - only trigger this if things are hidden
            this.gridApi.refreshCells({suppressFlash: true});
        },
        handleCellDoubleClicked(params) {
            // for debug, and testing out charting 
            console.log(params);  // just for debugging things
        },
        handleNewColumnsLoaded(params) {
            // from newColumnsLoaded grid event - we will call auto size method... but only if we have gridApi still, in case we reload entire thing
            const skipHeader = false;
            params.columnApi?.autoSizeAllColumns(null, skipHeader);
        },
        dumpGrid: function() {
            // just dump grid data to console for testing
            let gData = [];
            let allNodes = [];
            this.gridApi.forEachNode((node, index) => {
                allNodes.push(node);
                gData.push({
                    grid_index: index,
                    ...node.data,
                });
            });
            let columnState = this.gridColumnApi.getColumnState();
            let columnGroupState = this.gridColumnApi.getColumnGroupState();
            console.log('NODES:');
            console.log(allNodes);
            console.log('ROW DATA:');
            console.log(gData);
            console.log('getFirstDisplayedRow');
            console.log(this.gridApi.getFirstDisplayedRow());
            console.log('getLastDisplayedRow');
            console.log(this.gridApi.getLastDisplayedRow());
            console.log('getColumnState');
            console.log(columnState);
            console.log('getColumnGroupState');
            console.log(columnGroupState);
        },
        buildColDefs(apiCols) {
            // build colDefs from the raw api column data and return list of colDefs
            // TODO - for lookup cols, if there are empty values, use a sentinel, then a formatter to show blank formatted
            const colDefs = [];
            const makeColDef = (apiCol) => {
                // build a single column, recursively with children if any 
                const colDef = {};
                colDef['headerName'] = apiCol.label;
                // apiCol.coldef will have 'extra' stuff set in backend... attrs should match these exactly, so just pass them in 
                // should you put them up here or at the end?  here for now... no specific reason
                const colDefExtra = apiCol.coldef ?? {};
                Object.assign(colDef, colDefExtra);

                if (apiCol.children && Array.isArray(apiCol.children) && apiCol.children.length) {
                    colDef['groupId'] = apiCol.path;  // or api_key???
                    colDef['children'] = apiCol.children.map(ch => makeColDef(ch));
                } else {
                    // FOR REGULAR (non-group) COLUMNS
                    // -------------------------------------------
                    colDef['field'] = apiCol.path;
                    colDef['type'] = [apiCol.data_type];  // strings of the data type, set in backend
                    if (apiCol.path === 'title') {
                        colDef['type'].push('linkCell');  // make this the link cell to detail page
                    }
                    // LATER colDef['backendType'] = apiCol.data_type  // my own thing... change this
                    if (apiCol.options) {  // would be a dict/obj of {value: label}
                        colDef['refData'] = {...apiCol.options};
                    }
                }
                // HANDLE SPECIAL COLS
                if (apiCol.path === 'sm_state') {
                    // add special badge renderer to sm_state -- will always have this for any FlowObject list
                    colDef['cellRenderer'] = 'smStateRenderer';
                    colDef['filterParams'] = {
                        cellRenderer: 'smStateRenderer',
                    };
                }
                if (apiCol.path === 'title') {
                    const titleColDefExtras = {
                        // aggFunc: 'count',  # having aggFunc AND enabling any column for rowGroup causes problems... renderer gets confused, can't render the aggregated value - probably need something in the renderer to know if its aggregated
                        chartDataType: 'series',  // we use this as a count
                        enableRowGroup: false,
                        enablePivot: false,
                    };
                    Object.assign(colDef, titleColDefExtras);
                }
                return colDef;
            }
            apiCols.forEach(col => {
                colDefs.push(makeColDef(col));
            });
            // make the extra '_count' column for aggregations
            const countColDef = {
                field: '_count',
                headerName: 'Count',  // we'll leave this on - and keep it showing for now
                hide: false,  // toggle this on/off depending on pivotMode - hide must be false if using it for charting
                sort: 'desc',  // DEFAULT SORT BY THIS - IT WORKS WHEN SHOWING ROW GROUPS TOO
                aggFunc: 'count',
                chartDataType: 'series',
                suppressColumnsToolPanel: true,  // don't show in sidebar columns
                suppressFiltersToolPanel: true,  // don't show in sidebar filters
            }
            colDefs.push(countColDef);
            return colDefs;
        },
        makeChart(params) {
            // tesitng for now - NOT READY YET
            var createRangeChartParams = {
                cellRange: {
                    //rowStartIndex: 0,  // omit these to cover all rows
                    //rowEndIndex: 20,
                    columns: ['title', 'sm_state'],   //, 'title', '_cf_data.issue_type'],
                },
                chartType: 'groupedColumn',
                chartContainer: document.querySelector('#myChart'),
                aggFunc: 'count',
            };
            params.api.createRangeChart(createRangeChartParams);
        },
        // PRESETS
        stringifyFiltersGrouping(filters, grouping) {
            // pass in whatever you have for 'filters' and 'grouping' (separately, in consistent order) in a filter preset (even null/undefined) 
            // and we use it on the currently applied grid filterModel / grouping cols for change detection
            return JSON.stringify({filters, grouping});
        },
        handlePresetSelection(newPresetObj) {
            console.log('handlePresetSelection', newPresetObj);
            this.gridPresetSelected = cloneDeep(newPresetObj);
            // the v-model goes through here - must update this.gridPresetSelected
            // the ACTUAL preset values are inside the object.values
            const preset = cloneDeep(newPresetObj?.value ?? {});  // clone it right away to prevent problems
            console.log('new preset object', preset);
            // if null/falsey, that's OK -- just keep going to reset all existing filters/grouping
            // reset the GRID filters -- remove all first
            if (this.gridApi) {  
                // put stuff here that needs gridApi
                this.gridApi.setFilterModel(null);  // setFilterModel(null) is how its done in ag-grid docs
            }
            // reset grouping
            if (this.gridColumnApi) {
                this.gridColumnApi.setRowGroupColumns([]);
            }
            this.gridApi?.onFilterChanged();  // and make sure the grid knows we updated

            // get the values for new filters/grouping -- and stringify it before setting anything (for change detection)
            const newFilters = preset?.filters ?? null;  // default to null - that's what docs say for reset (not {})
            const newGrouping = preset?.grouping ?? [];            
            // NOTE - if you set a preset that includes a 'set' filter, and it has values that there is NO DATA FOR, THE GRID REMOVES THE VALUE
            // SO IF YOU COMPARE THE PRESET TO THE ACTUAL FILTER, IT MIGHT BE DIFFERENT, JUST BECAUSE THE GRID REMOVED THAT VALUE!
            // SO.. we will capture the new filters in here JUST AFTER SETTING THEM - NOT BEFORE 
            if (newFilters && this.gridApi) {
            //if (newFilters &&this.gridApi) {
                console.log('setting THESE newFilters', JSON.stringify(newFilters));
                //console.log('setting these FIXED FILTERS', fixedFilters);
                // maybe check if its already filtered for that row, if so, then destroy it and recreated it
                this.gridApi.setFilterModel(newFilters);
            }
            if (newGrouping && this.gridColumnApi) {
            //if (this.gridColumnApi) {
                this.gridColumnApi.setRowGroupColumns(newGrouping);
            } else {
                // only autosize if we did NOT group?  or else it will make all cols very narrow...
                this.autoSizeAll();
            }
            // finally, we must tell the grid the filters changed -- docs say to do this
            this.gridApi?.onFilterChanged();
            // NOW... capture the 'last set' filters -- must do it AFTER passing filter to the grid, because it removes values from 'set' filters that are not valid 
            const currentFilters = this.gridApi?.getFilterModel() ?? {};  // returns {} if no filters
            const currentGroupedColIds = this.gridColumnApi?.getRowGroupColumns()?.map(c => c.colId) ?? [];
            this.lastSetFiltersGroupingStringified = this.stringifyFiltersGrouping(currentFilters, currentGroupedColIds);
            this.lastSetFiltersGrouping = cloneDeep({filters: currentFilters, grouping: currentGroupedColIds});  // default to {} for filters, so comparison works
        },
        resetPresetSelection() {
            // set this.gridPresetSelected to orignal value to clear the cascade select
            this.gridPresetSelected = null;  // does NOT trigger our handlePresetSelection... we do it here
            this.handlePresetSelection(this.gridPresetSelected);
        },
        // EXTERNAL FILTERS
        // call this.gridApi.onFilterChanged(); after making changes to things that affect filters
        // more details: https://www.ag-grid.com/archive/25.3.0/vue-grid/filter-external/
        isExternalFilterPresent() {
            // return boolean -- pass this to the grid 
            return (this.timeFilterKey !== this.timeFilterReset) || (this.userRoleKey !== this.userRoleReset);
        },
        doesExternalFilterPass(node) {
            // another callback to pass to the grid - should return boolean for whether a node passes (data is in node.data)
            return (!this.minJsDate || node.data.createdTenantTzJs >= this.minJsDate ) && (!this.maxJsDate || node.data.createdTenantTzJs < this.maxJsDate ) && (!this.onlyHasObjectRole || node.data.has_object_role);
        },
        handleExternalTimeFilterChanged(newFilterKey) {
            // get the new key, then set appropriate min/max values, then call this.handleExternalFilterChanged() to trigger things
            // PUT THE v-model THROUGH HERE - so we can NOT update it if we don't have a valid key
            const nowTenantTz = this.nowTenantTz();  // from tenantInfoMixin - returns Luxon DateTime 
            if (!newFilterKey) {
                this.minJsDate = null;
                this.maxJsDate = null;
                this.timeFilterKey = newFilterKey;
            } else if (newFilterKey === this.timeFilterReset) {
                this.minJsDate = null;  // TODO - get all these from the options array!
                this.maxJsDate = null;
                this.timeFilterKey = this.timeFilterReset;
            } else if (newFilterKey === 'today') {
                this.minJsDate = nowTenantTz.startOf('day').toJSDate();
                this.maxJsDate = null;
                this.timeFilterKey = newFilterKey;
            } else if (newFilterKey === 'yesterday') {
                const yesterday = nowTenantTz.minus({days: 1});
                this.minJsDate = yesterday.startOf('day').toJSDate();
                this.maxJsDate = yesterday.endOf('day').toJSDate();
                this.timeFilterKey = newFilterKey;
            } else if (newFilterKey === 'last48hours') {  // last full day + today
                this.minJsDate = nowTenantTz.minus({hours: 48}).toJSDate();
                this.maxJsDate = null;
                this.timeFilterKey = newFilterKey;
            } else if (newFilterKey === 'last1week') {  // last 6 full days + today
                this.minJsDate = nowTenantTz.minus({days: 7}).toJSDate();
                this.maxJsDate = null;
                this.timeFilterKey = newFilterKey;
            } else if (newFilterKey === 'last1month') {  // last  full days + today
                this.minJsDate = nowTenantTz.minus({months: 1}).toJSDate();
                this.maxJsDate = null;
                this.timeFilterKey = newFilterKey;
            } else {
                console.error('Have a time filter key that is not valid', newFilterKey);
                this.timeFilterKey = this.timeFilterReset;  // set it null to avoid confusion
            }
            this.handleExternalFilterChanged();
        }, 
        handleExternalRoleFilterChanged(newUserRoleKey) {
            if (!newUserRoleKey) {
                this.onlyHasObjectRole = false;  // we use this in the actual filter...
                this.userRoleKey = this.userRoleReset;
            } else if (newUserRoleKey === this.userRoleReset) {
                this.onlyHasObjectRole = false;  // TODO - get all these from the options array!
                this.userRoleKey = this.userRoleReset;
            } else if (newUserRoleKey === 'has_object_role') {
                this.onlyHasObjectRole = true;
                this.userRoleKey = newUserRoleKey;
            }
            this.handleExternalFilterChanged();
        },
        handleExternalFilterChanged() {
            // update local values for filters, then call this.gridApi.onFilterChanged();
            const newFilterObj = {};
            if (this.timeFilterKey) {
                newFilterObj['created_tenant_tz']
            }
            this.gridApi?.onFilterChanged();
        },
        resetExternalFilters() {
            this.handleExternalTimeFilterChanged(this.timeFilterReset);
            this.handleExternalRoleFilterChanged(this.userRoleReset);
        },
        // FILTERS
        resetFilters() {
            // might get called after grid is destroyed, or before its creatd - so might not have this.gridApi
            if (this.gridApi) {  
                // put stuff here that needs gridApi
                this.gridApi.setFilterModel(null);
            }
            this.resetExternalFilters();
            this.resetPresetSelection();
            this.autoSizeAll();
        },
        handleFilterChanged: function(params) {  // can take event as arg, if you need it
            // callback that handles the grid filterChanged event - need to compare with our own 
            // but this is tricky... needs work
            // need to make sure that if user manually changes things we filter via api, that the dropdown selections change? that will be hard...  
            // USE THIS FOR CHANGE DETECTION
            console.log('handleFilterChanged', params);
            const currentFilters = this.gridApi.getFilterModel();  // returns {} if no filters
            this.currentFilters = Object.assign({}, cloneDeep(currentFilters)); 
            console.log('setting currentFilters', currentFilters);
        },
        
        handleColumnRowGroupChanged(event) {   
            // callback from the grid, when any row grouping is changed - need to check, because maybe our other method triggered it
            console.log('columnRowGroupChanged', event);
            // if event.source === 'api' that was us doing it from here, not the user
            // get event.columns and fields from that to see what fields are grouped
            // USE THIS FOR CHANGE DETECTION
            const groupedColIds = event.columns.length ? event.columns.map(c => c.colId) : [];
            this.currentGroupingCols = [...groupedColIds];
        },
        updatePagination(newPageSize) {
            console.log('new paginationPageSize', newPageSize);
            if (newPageSize && this.gridApi) {
                this.paginationPageSize = Number(newPageSize);
                this.gridApi.paginationSetPageSize(newPageSize);  
            }
        },
    }
}
</script>

<style lang="scss">

.flow-object-filter-list {
    .quick-filter {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        .filter-label {
            display: block;
            color: $gray-500;
            margin-bottom: 0.2rem;
            text-transform: uppercase;
            font-size: 0.7rem;
            font-weight: 500;
        }
        // PrimeVue components
        .p-cascadeselect-item-text,
        .p-cascadeselect-label,
        .p-dropdown-item,
        .p-dropdown-label {
            font-size: 0.8rem;
        }
    }

    .ag-header-row {
        .ag-header-icon.ag-header-label-icon.ag-filter-icon > .ag-icon.ag-icon-filter {
            color: red;
        }
    }

    .number-cell {
        text-align: right;
    }
    .centered-cell {
        text-align: center;
    }
}

</style>